import { faBoxArchive, faTruckFast, faBoxOpen } from '@fortawesome/pro-light-svg-icons';
import Flex from 'components/ui/Flex';
import ActionButton from 'components/ui/ActionButton';
import { BatchReturnStatusType } from 'returnCenter-api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Text from 'components/ui/Text';

type BatchReturnActionPanelProps = {
    status?: BatchReturnStatusType;
    isSender?: boolean;
    onClose: () => void;
    onMarkAsSent: () => void;
    onMarkAsReceived: () => void;
    isLoading?: boolean;
    returnStatus: boolean;
};

const BatchReturnActionPanel = ({
    status,
    isSender = false,
    onClose,
    onMarkAsSent,
    onMarkAsReceived,
    isLoading = false,
    returnStatus,
}: BatchReturnActionPanelProps) => {
    const { t } = useTranslation();

    const isOpen = status === BatchReturnStatusType.Open;
    const isClosed = status === BatchReturnStatusType.Closed;
    const isSent = status === BatchReturnStatusType.Sent;
    const isPartiallyReceived = status === BatchReturnStatusType.PartiallyReceived;
    const isReceived = status != null && status >= BatchReturnStatusType.Received;

    const disableAction = isLoading || !returnStatus;

    if (!isOpen && !isClosed && !isSent && !isPartiallyReceived && !isReceived) {
        return null;
    }

    return (
        <Flex column gap="1rem">
            <Panel className="p-3" column gap="0.5rem">
                <NextStepLabel>{t('nextStepInFlow', 'Next step')}</NextStepLabel>
                {isOpen && (
                    <ActionButton
                        label={t('batchReturn.closeBatch', 'Close batch')}
                        icon={faBoxArchive}
                        onClick={onClose}
                        disabled={disableAction}
                        isLoading={isLoading}
                        main
                        fullWidth
                    />
                )}
                {isClosed && (
                    <ActionButton
                        label={t('batchReturn.markAsSent', 'Mark as sent')}
                        icon={faTruckFast}
                        onClick={onMarkAsSent}
                        disabled={disableAction}
                        isLoading={isLoading}
                        main
                        fullWidth
                    />
                )}
                {(isSent || isPartiallyReceived) && (
                    <ActionButton
                        label={t('batchReturn.markAsReceived', 'Mark as received')}
                        icon={faBoxOpen}
                        onClick={onMarkAsReceived}
                        disabled={disableAction || isSender}
                        isLoading={isLoading}
                        main
                        fullWidth
                    />
                )}
            </Panel>
        </Flex>
    );
};

export default BatchReturnActionPanel;

const Panel = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
`;

const NextStepLabel = styled(Text)`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textLight};
    margin-bottom: 0.25rem;
`;
